(() => {
  'use strict';

  const SECTION_SELECTOR =
    '#shopify-section-tiktok-v2-staging';

  const REVIEWS_SELECTOR =
    '.almaxi-tt__reviews';

  const ROW_SELECTOR =
    '.almaxi-tt__reviews-row';

  const CARD_SELECTOR =
    '.almaxi-tt__review';

  const MOBILE_QUERY =
    '(max-width: 767.98px)';

  const AUTOPLAY_DELAY = 2800;
  const USER_RESUME_DELAY = 4000;

  const mobileMedia =
    window.matchMedia(MOBILE_QUERY);

  const reduceMotionMedia =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let destroyCarousel = null;

  const initCarousel = () => {
    if (!mobileMedia.matches) return null;

    const section =
      document.querySelector(SECTION_SELECTOR);

    if (!section) return null;

    const row =
      section.querySelector(ROW_SELECTOR);

    if (!row) return null;

    const reviewsBlock =
      section.querySelector(REVIEWS_SELECTOR) ||
      row;

    const cards = Array.from(
      row.querySelectorAll(CARD_SELECTOR)
    );

    if (cards.length < 2) return null;

    if (
      section.dataset.almMobileReviewsReady ===
      'true'
    ) {
      return null;
    }

    section.dataset.almMobileReviewsReady =
      'true';

    const controller =
      new AbortController();

    const { signal } = controller;

    let activeIndex = 0;
    let direction = 1;
    let userPaused = false;
    let autoplayTimer = 0;
    let resumeTimer = 0;
    let scrollFrame = 0;

    const dots = document.createElement('div');

    dots.className =
      'alm-review-carousel-dots';

    dots.setAttribute(
      'aria-label',
      'Customer review navigation'
    );

    const dotButtons = cards.map(
      (_, index) => {
        const button =
          document.createElement('button');

        button.type = 'button';
        button.className =
          'alm-review-carousel-dot';

        button.setAttribute(
          'aria-label',
          `Show customer review ${index + 1}`
        );

        dots.appendChild(button);
        return button;
      }
    );

    row.insertAdjacentElement(
      'afterend',
      dots
    );

    const clearAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
    };

    const clearResumeTimer = () => {
      window.clearTimeout(resumeTimer);
      resumeTimer = 0;
    };

    const isActuallyInView = () => {
      const rect =
        reviewsBlock.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight;

      if (!viewportHeight) return false;

      const visibleTop =
        Math.max(rect.top, 0);

      const visibleBottom =
        Math.min(rect.bottom, viewportHeight);

      const visibleHeight =
        Math.max(
          0,
          visibleBottom - visibleTop
        );

      const requiredVisibleHeight =
        Math.min(
          120,
          Math.max(60, rect.height * 0.18)
        );

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        visibleHeight >= requiredVisibleHeight
      );
    };

    const setActive = (index) => {
      activeIndex = Math.max(
        0,
        Math.min(index, cards.length - 1)
      );

      cards.forEach((card, cardIndex) => {
        const active =
          cardIndex === activeIndex;

        card.classList.toggle(
          'alm-review-card-active',
          active
        );

        card.setAttribute(
          'aria-hidden',
          active ? 'false' : 'true'
        );
      });

      dotButtons.forEach(
        (button, buttonIndex) => {
          const active =
            buttonIndex === activeIndex;

          button.classList.toggle(
            'is-active',
            active
          );

          if (active) {
            button.setAttribute(
              'aria-current',
              'true'
            );
          } else {
            button.removeAttribute(
              'aria-current'
            );
          }
        }
      );
    };

    const getNearestIndex = () => {
      const rowRect =
        row.getBoundingClientRect();

      let nearestIndex = 0;
      let nearestDistance = Infinity;

      cards.forEach((card, index) => {
        const cardRect =
          card.getBoundingClientRect();

        const distance =
          Math.abs(
            cardRect.left - rowRect.left
          );

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    };

    const scrollToIndex = (
      index,
      behavior = 'smooth'
    ) => {
      const target = cards[index];
      if (!target) return;

      const rowRect =
        row.getBoundingClientRect();

      const targetRect =
        target.getBoundingClientRect();

      const targetLeft =
        row.scrollLeft +
        targetRect.left -
        rowRect.left;

      row.scrollTo({
        left: targetLeft,
        behavior
      });

      setActive(index);
    };

    const canAutoplay = () =>
      isActuallyInView() &&
      !userPaused &&
      !reduceMotionMedia.matches &&
      document.visibilityState === 'visible';

    const scheduleAutoplay = () => {
      clearAutoplay();

      if (!canAutoplay()) return;

      autoplayTimer =
        window.setTimeout(
          () => {
            /*
              Re-check actual geometry at the
              exact moment the timer fires.
            */
            if (!canAutoplay()) {
              clearAutoplay();
              return;
            }

            let next =
              activeIndex + direction;

            if (next >= cards.length) {
              direction = -1;
              next = cards.length - 2;
            } else if (next < 0) {
              direction = 1;
              next = 1;
            }

            scrollToIndex(next);
            scheduleAutoplay();
          },
          AUTOPLAY_DELAY
        );
    };

    const pauseForUser = () => {
      userPaused = true;
      clearAutoplay();
      clearResumeTimer();
    };

    const resumeAfterUser = () => {
      clearResumeTimer();

      resumeTimer =
        window.setTimeout(
          () => {
            userPaused = false;
            scheduleAutoplay();
          },
          USER_RESUME_DELAY
        );
    };

    const handleViewportChange = () => {
      if (isActuallyInView()) {
        scheduleAutoplay();
      } else {
        clearAutoplay();
      }
    };

    row.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) {
          cancelAnimationFrame(scrollFrame);
        }

        scrollFrame =
          requestAnimationFrame(() => {
            const nearest =
              getNearestIndex();

            if (nearest !== activeIndex) {
              setActive(nearest);
            }

            scrollFrame = 0;
          });
      },
      {
        passive: true,
        signal
      }
    );

    row.addEventListener(
      'pointerdown',
      pauseForUser,
      {
        passive: true,
        signal
      }
    );

    row.addEventListener(
      'pointerup',
      resumeAfterUser,
      {
        passive: true,
        signal
      }
    );

    row.addEventListener(
      'pointercancel',
      resumeAfterUser,
      {
        passive: true,
        signal
      }
    );

    dotButtons.forEach(
      (button, index) => {
        button.addEventListener(
          'click',
          () => {
            pauseForUser();

            direction =
              index >= activeIndex ? 1 : -1;

            scrollToIndex(index);
            resumeAfterUser();
          },
          { signal }
        );
      }
    );

    /*
      Geometry-based scroll/resize guard.
      This is the authoritative visibility
      control for Shopify mobile preview.
    */
    window.addEventListener(
      'scroll',
      handleViewportChange,
      {
        passive: true,
        signal
      }
    );

    window.addEventListener(
      'resize',
      handleViewportChange,
      {
        passive: true,
        signal
      }
    );

    /*
      IntersectionObserver remains as a
      secondary efficiency signal.
    */
    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              const entry = entries[0];

              if (
                entry.isIntersecting &&
                isActuallyInView()
              ) {
                scheduleAutoplay();
              } else {
                clearAutoplay();
              }
            },
            {
              threshold: [
                0,
                0.18,
                0.5
              ],
              rootMargin:
                '0px 0px -4% 0px'
            }
          )
        : null;

    if (observer) {
      observer.observe(reviewsBlock);
    }

    const handleVisibility = () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        handleViewportChange();
      } else {
        clearAutoplay();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibility,
      { signal }
    );

    const handleReducedMotion = () => {
      if (reduceMotionMedia.matches) {
        clearAutoplay();
      } else {
        handleViewportChange();
      }
    };

    if (
      typeof reduceMotionMedia
        .addEventListener === 'function'
    ) {
      reduceMotionMedia.addEventListener(
        'change',
        handleReducedMotion,
        { signal }
      );
    }

    setActive(getNearestIndex());

    requestAnimationFrame(() => {
      setActive(getNearestIndex());
      handleViewportChange();
    });

    return () => {
      clearAutoplay();
      clearResumeTimer();

      if (scrollFrame) {
        cancelAnimationFrame(scrollFrame);
      }

      if (observer) {
        observer.disconnect();
      }

      controller.abort();
      dots.remove();

      cards.forEach((card) => {
        card.classList.remove(
          'alm-review-card-active'
        );

        card.removeAttribute(
          'aria-hidden'
        );
      });

      delete section.dataset
        .almMobileReviewsReady;

      row.scrollLeft = 0;
    };
  };

  const syncCarousel = () => {
    if (mobileMedia.matches) {
      if (!destroyCarousel) {
        destroyCarousel =
          initCarousel();
      }
    } else if (destroyCarousel) {
      destroyCarousel();
      destroyCarousel = null;
    }
  };

  const boot = () => {
    syncCarousel();

    if (
      typeof mobileMedia
        .addEventListener === 'function'
    ) {
      mobileMedia.addEventListener(
        'change',
        syncCarousel
      );
    } else if (
      typeof mobileMedia.addListener ===
      'function'
    ) {
      mobileMedia.addListener(
        syncCarousel
      );
    }
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }
})();
